import React, { useState, useContext } from 'react';
import AppContext from '../../AppContext';
import { DEFAULT_QR_CODE_URL } from '../../config';
import { useLanguage } from '../../contexts/LanguageContext';

function PrintTestModal({ onClose, settings }) {
    const { theme } = useContext(AppContext);
    const { t } = useLanguage();

    const [selectedWidth, setSelectedWidth] = useState(32);
    const [selectedBaudRate, setSelectedBaudRate] = useState(9600);
    const [selectedFontSize, setSelectedFontSize] = useState('normal');
    const [selectedPrinterType, setSelectedPrinterType] = useState('bluetooth'); // 'bluetooth' or 'serial'
    const [testStatus, setTestStatus] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [diagnosticInfo, setDiagnosticInfo] = useState('');

    // Test section toggles
    const [testSections, setTestSections] = useState({
        patterns: true,
        alignment: true,
        columns: true,
        border: true,
        qrCode: true
    });

    // Check browser compatibility
    const isSerialSupported = 'serial' in navigator;
    const isBluetoothSupported = 'bluetooth' in navigator;

    // Run diagnostics on mount
    React.useEffect(() => {
        const checkEnvironment = () => {
            const info = [];
            info.push(`Browser: ${navigator.userAgent}`);
            info.push(`HTTPS: ${window.location.protocol === 'https:' ? 'Yes' : 'No'}`);
            info.push(`Localhost: ${window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'Yes' : 'No'}`);
            info.push(`Serial API: ${('serial' in navigator) ? 'Available ✅' : 'NOT AVAILABLE ❌'}`);
            info.push(`Bluetooth API: ${('bluetooth' in navigator) ? 'Available ✅' : 'NOT AVAILABLE ❌'}`);

            const issues = [];
            if (!('serial' in navigator) && !('bluetooth' in navigator)) {
                issues.push(`\n⚠️ Neither Web Serial nor Web Bluetooth API is available.`);
                issues.push(`Possible reasons:`);
                issues.push(`1. Not using HTTPS (required for non-localhost)`);
                issues.push(`2. Browser doesn't support these APIs`);
                issues.push(`3. Features disabled in browser settings`);
                issues.push(`4. Running in iframe or embedded context`);
                info.push(...issues);
            } else if (!('bluetooth' in navigator)) {
                issues.push(`\n⚠️ Web Bluetooth API is not available.`);
                issues.push(`Try using USB/Serial printer instead.`);
                info.push(...issues);
            }

            setDiagnosticInfo(info.join('\n'));
            console.log('Printer Diagnostics:', info.join('\n'));
        };
        checkEnvironment();
    }, []);

    const widthOptions = [
        { value: 24, label: '24 chars (Narrow - 58mm)', description: 'Best for very small receipts' },
        { value: 28, label: '28 chars (58mm Standard)', description: 'Recommended for GOOJPRT PT210' },
        { value: 32, label: '32 chars (58mm Wide)', description: 'Current setting' },
        { value: 42, label: '42 chars (80mm)', description: 'For 80mm printers' }
    ];

    const baudRateOptions = [
        { value: 9600, label: '9600 baud', description: 'Standard speed' },
        { value: 19200, label: '19200 baud', description: 'Common for PT210' },
        { value: 38400, label: '38400 baud', description: 'High speed' },
        { value: 115200, label: '115200 baud', description: 'Very high speed' }
    ];

    const fontSizeOptions = [
        { value: 'small', label: t('small'), description: 'Compact text' },
        { value: 'normal', label: t('normal'), description: 'Standard size' },
        { value: 'large', label: t('large'), description: 'Larger text' }
    ];

    const printerTypeOptions = [
        { value: 'bluetooth', label: 'Bluetooth Printer', description: 'Wireless thermal printer (most common)', enabled: isBluetoothSupported },
        { value: 'serial', label: 'USB/Serial Printer', description: 'Wired thermal printer', enabled: isSerialSupported }
    ];

    const generateTestReceipt = (width) => {
        const separator = '='.repeat(width);
        const dashLine = '-'.repeat(width);

        const centerText = (text, width) => {
            const totalPadding = Math.max(0, width - text.length);
            const leftPadding = Math.floor(totalPadding / 2);
            const rightPadding = totalPadding - leftPadding;
            return ' '.repeat(leftPadding) + text + ' '.repeat(rightPadding);
        };

        const leftRight = (left, right, width) => {
            const spaces = Math.max(1, width - left.length - right.length);
            return left + ' '.repeat(spaces) + right;
        };

        let receipt = '\x1B\x40'; // Initialize printer
        receipt += '\x1B\x74\x00'; // Set character code table to CP437 (default)

        // Font size commands
        if (selectedFontSize === 'small') {
            receipt += '\x1D\x21\x00'; // Normal size
        } else if (selectedFontSize === 'large') {
            receipt += '\x1D\x21\x11'; // Double height and width
        } else {
            receipt += '\x1D\x21\x01'; // Double height only
        }

        // Header
        receipt += '\x1B\x61\x01'; // Center align
        receipt += '\x1B\x45\x01'; // Bold on
        receipt += 'PRINTER TEST\n';
        receipt += '\x1B\x45\x00'; // Bold off
        receipt += 'Width: ' + width + ' chars\n';
        receipt += 'Type: ' + selectedPrinterType + '\n';
        receipt += 'Font: ' + selectedFontSize + '\n';
        receipt += '\x1B\x61\x00'; // Left align
        receipt += separator + '\n\n';

        // Test patterns (conditional)
        if (testSections.patterns) {
            receipt += '\x1B\x45\x01TEST PATTERNS:\x1B\x45\x00\n';
            receipt += dashLine + '\n';

            // Numbers test
            receipt += '1234567890'.repeat(Math.ceil(width / 10)).substring(0, width) + '\n';

            // Alphabet test
            receipt += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.substring(0, width) + '\n';
            receipt += 'abcdefghijklmnopqrstuvwxyz'.substring(0, width) + '\n';

            // Spanish characters (using simple ASCII alternatives)
            receipt += 'Spanish: n a e i o u N A E I O U\n';

            // Special characters (using safe ASCII)
            receipt += 'Symbols: $ @ # % & * ( ) + - = /\n';
            receipt += dashLine + '\n\n';
        }

        // Alignment test (conditional)
        if (testSections.alignment) {
            receipt += '\x1B\x45\x01ALIGNMENT TEST:\x1B\x45\x00\n';
            receipt += '\x1B\x61\x00Left aligned text\n';
            receipt += '\x1B\x61\x01Center aligned text\n';
            receipt += '\x1B\x61\x02Right aligned text\n';
            receipt += '\x1B\x61\x00'; // Back to left
            receipt += dashLine + '\n\n';
        }

        // Column test (conditional)
        if (testSections.columns) {
            receipt += '\x1B\x45\x01COLUMN TEST:\x1B\x45\x00\n';
            receipt += leftRight('Item', 'Price', width) + '\n';
            receipt += dashLine + '\n';
            receipt += leftRight('Coffee', '$2.50', width) + '\n';
            receipt += leftRight('Water', '$1.50', width) + '\n';
            receipt += leftRight('Total', '$4.00', width) + '\n';
            receipt += dashLine + '\n\n';
        }

        // Border test (conditional, using simple ASCII)
        if (testSections.border) {
            receipt += '\x1B\x45\x01BORDER TEST:\x1B\x45\x00\n';
            receipt += '+' + '-'.repeat(width - 2) + '+\n';
            receipt += '|' + centerText('Bordered Text', width - 2) + '|\n';
            receipt += '+' + '-'.repeat(width - 2) + '+\n';
            receipt += separator + '\n\n';
        }

        // QR Code test (conditional, ESC/POS QR code commands)
        if (testSections.qrCode) {
            receipt += '\x1B\x61\x01'; // Center align
            receipt += '\x1B\x45\x01QR CODE TEST\x1B\x45\x00\n';
            receipt += '\n';

            // Generate QR Code using ESC/POS commands
            const qrData = settings?.qrCodeUrl || DEFAULT_QR_CODE_URL;
            const qrDataLength = qrData.length + 3;
            const pL = qrDataLength % 256;
            const pH = Math.floor(qrDataLength / 256);

            // QR Code commands for ESC/POS
            receipt += '\x1D\x28\x6B\x04\x00\x31\x41\x32\x00'; // Function 165: Select QR code model (Model 2)
            receipt += '\x1D\x28\x6B\x03\x00\x31\x43\x05'; // Function 167: Set module size (5)
            receipt += '\x1D\x28\x6B\x03\x00\x31\x45\x30'; // Function 169: Set error correction level (L = 48/0x30)
            receipt += '\x1D\x28\x6B' + String.fromCharCode(pL, pH) + '\x31\x50\x30' + qrData; // Function 180: Store QR data
            receipt += '\x1D\x28\x6B\x03\x00\x31\x51\x30'; // Function 181: Print QR code

            receipt += '\n\n';
            const qrText = settings?.qrCodeText || 'Escanea para visitar:';
            receipt += qrText + '\n';
            receipt += '\x1B\x61\x00'; // Left align
            receipt += separator + '\n\n';
        }

        // Feed and cut
        receipt += '\n\n\n\n';
        receipt += '\x1D\x56\x41\x03'; // Partial cut

        return receipt;
    };

    const handleBluetoothPrint = async () => {
        if (!('bluetooth' in navigator)) {
            // Check if iOS device
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
            if (isIOS) {
                throw new Error('Safari/Chrome on iOS do not support Bluetooth printing. Please install Bluefy Browser from the App Store: https://apps.apple.com/app/bluefy-web-ble-browser/id1492822055 - Then open this website in Bluefy to use Bluetooth printing.');
            }
            throw new Error('Web Bluetooth API not supported. Please use Chrome, Edge, or Opera browser (version 89+) on a desktop or Android device.');
        }

        try {
            // Request Bluetooth device
            const device = await navigator.bluetooth.requestDevice({
                filters: [
                    { services: ['000018f0-0000-1000-8000-00805f9b34fb'] }, // ESC/POS service
                    { name: 'GOOJPRT' },
                    { namePrefix: 'PT' },
                    { namePrefix: 'Printer' }
                ],
                optionalServices: ['000018f0-0000-1000-8000-00805f9b34fb']
            });

            // Connect to GATT server
            const server = await device.gatt.connect();

            // Get printer service (ESC/POS service UUID)
            const service = await server.getPrimaryService('000018f0-0000-1000-8000-00805f9b34fb');

            // Get write characteristic
            const characteristic = await service.getCharacteristic('00002af1-0000-1000-8000-00805f9b34fb');

            // Generate test receipt
            const receipt = generateTestReceipt(selectedWidth);
            const encoder = new TextEncoder();
            const data = encoder.encode(receipt);

            // Send data in chunks (Bluetooth LE has packet size limits ~20 bytes)
            const chunkSize = 20;
            for (let i = 0; i < data.length; i += chunkSize) {
                const chunk = data.slice(i, i + chunkSize);
                await characteristic.writeValue(chunk);
                // Small delay between chunks
                await new Promise(resolve => setTimeout(resolve, 10));
            }

            // Disconnect
            device.gatt.disconnect();

        } catch (error) {
            if (error.name === 'NotFoundError') {
                throw new Error('No Bluetooth printer found. Make sure your printer is powered on and in pairing mode.');
            }
            throw error;
        }
    };

    const handleSerialPrint = async () => {
        if (!('serial' in navigator)) {
            // Check if iOS device
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
            if (isIOS) {
                throw new Error('Serial/USB printing is not supported on iOS devices. Please use an Android device, Windows PC, or Mac with Chrome/Edge browser.');
            }
            throw new Error('Web Serial API not supported. Please use Chrome, Edge, or Opera browser (version 89+) on a desktop or Android device.');
        }

        // Request serial port access
        const port = await navigator.serial.requestPort();

        // Open port with selected baud rate
        await port.open({
            baudRate: selectedBaudRate,
            dataBits: 8,
            stopBits: 1,
            parity: 'none',
            flowControl: 'none'
        });

        const encoder = new TextEncoder();
        const writer = port.writable.getWriter();

        try {
            // Generate and send test receipt
            const receipt = generateTestReceipt(selectedWidth);
            await writer.write(encoder.encode(receipt));
        } finally {
            // Release writer and close port
            writer.releaseLock();
            await port.close();
        }
    };

    const handleTestPrint = async () => {
        setIsLoading(true);
        setTestStatus(t('printing') + '...');

        try {
            if (selectedPrinterType === 'bluetooth') {
                await handleBluetoothPrint();
            } else {
                await handleSerialPrint();
            }

            setTestStatus(t('success') + '! ' + t('checkPrinterOutput'));
            setTimeout(() => setTestStatus(''), 3000);

        } catch (error) {
            console.error('Print test error:', error);

            let errorMessage = error.message;

            // Provide user-friendly error messages
            if (error.name === 'NotFoundError') {
                errorMessage = 'No printer selected. Please select a printer when prompted.';
            } else if (error.name === 'SecurityError') {
                errorMessage = 'Access denied. Please ensure HTTPS is enabled or using localhost.';
            } else if (error.name === 'NetworkError') {
                errorMessage = 'Connection failed. Please check printer is connected and powered on.';
            } else if (!('serial' in navigator)) {
                errorMessage = 'Web Serial API not supported. Use Chrome/Edge 89+';
            }

            setTestStatus(t('error') + ': ' + errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleQuickTest = async (width) => {
        setSelectedWidth(width);
        setTimeout(() => handleTestPrint(), 100);
    };

    const handleSaveConfig = () => {
        // Save configuration to localStorage
        const config = {
            width: selectedWidth,
            baudRate: selectedBaudRate,
            fontSize: selectedFontSize
        };
        localStorage.setItem('printerConfig', JSON.stringify(config));
        setTestStatus(t('settingsSaved'));
        setTimeout(() => setTestStatus(''), 2000);
    };

    const modalStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem'
    };

    const contentStyle = {
        backgroundColor: theme?.colors?.surface || '#fff',
        borderRadius: theme?.borderRadius || 8,
        padding: '1.5rem',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: theme?.boxShadow || '0 4px 6px rgba(0,0,0,0.1)',
        color: theme?.colors?.text || '#000'
    };

    const sectionStyle = {
        marginBottom: '1.5rem',
        padding: '1rem',
        backgroundColor: theme?.colors?.background || '#f5f5f5',
        borderRadius: theme?.borderRadius || 8,
        border: `1px solid ${theme?.colors?.border || '#ddd'}`
    };

    const buttonStyle = {
        padding: '0.75rem 1.5rem',
        margin: '0.25rem',
        backgroundColor: theme?.colors?.primary || '#1976d2',
        color: theme?.colors?.surface || '#fff',
        border: 'none',
        borderRadius: theme?.borderRadius || 8,
        cursor: 'pointer',
        fontWeight: 'bold',
        fontSize: '1rem'
    };

    const quickTestButtonStyle = {
        ...buttonStyle,
        backgroundColor: theme?.colors?.secondary || '#dc004e',
        padding: '0.5rem 1rem',
        fontSize: '0.9rem'
    };

    return (
        <div style={modalStyle} onClick={onClose}>
            <div style={contentStyle} onClick={(e) => e.stopPropagation()}>
                <h2 style={{
                    marginTop: 0,
                    color: theme?.colors?.text || '#000',
                    borderBottom: `2px solid ${theme?.colors?.primary || '#1976d2'}`,
                    paddingBottom: '0.5rem'
                }}>
                    🖨️ {t('printerTestMode')}
                </h2>

                {/* Browser Compatibility Warning */}
                {(!isSerialSupported && !isBluetoothSupported) && (
                    <div style={{
                        padding: '1rem',
                        marginBottom: '1rem',
                        backgroundColor: theme?.colors?.errorLight || '#ffebee',
                        color: theme?.colors?.error || '#d32f2f',
                        borderRadius: theme?.borderRadius || 8,
                        border: `2px solid ${theme?.colors?.error || '#d32f2f'}`,
                        fontWeight: 'bold',
                        textAlign: 'center'
                    }}>
                        ⚠️ {t('error')}: Neither Web Serial nor Web Bluetooth API is supported.
                        <br />
                        Please use Chrome, Edge, or Opera (version 89+) for thermal printing.
                    </div>
                )}

                {/* Quick Test Buttons */}
                <div style={sectionStyle}>
                    <h3 style={{ marginTop: 0, color: theme?.colors?.text }}>⚡ {t('quickTests')}</h3>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <button
                            style={quickTestButtonStyle}
                            onClick={() => handleQuickTest(24)}
                            disabled={isLoading || (!isSerialSupported && !isBluetoothSupported)}
                        >
                            {t('test')} 24 {t('chars')}
                        </button>
                        <button
                            style={quickTestButtonStyle}
                            onClick={() => handleQuickTest(28)}
                            disabled={isLoading || (!isSerialSupported && !isBluetoothSupported)}
                        >
                            {t('test')} 28 {t('chars')} (PT210)
                        </button>
                        <button
                            style={quickTestButtonStyle}
                            onClick={() => handleQuickTest(32)}
                            disabled={isLoading || (!isSerialSupported && !isBluetoothSupported)}
                        >
                            {t('test')} 32 {t('chars')}
                        </button>
                    </div>
                </div>

                {/* Printer Type Selection */}
                <div style={sectionStyle}>
                    <h3 style={{ marginTop: 0, color: theme?.colors?.text }}>🔌 {t('printerType')}</h3>
                    {printerTypeOptions.map(option => (
                        <div key={option.value} style={{ marginBottom: '0.5rem' }}>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: option.enabled ? 'pointer' : 'not-allowed',
                                padding: '0.5rem',
                                backgroundColor: selectedPrinterType === option.value ?
                                    (theme?.colors?.primaryLight || '#e3f2fd') : 'transparent',
                                borderRadius: theme?.borderRadius || 4,
                                opacity: option.enabled ? 1 : 0.5
                            }}>
                                <input
                                    type="radio"
                                    value={option.value}
                                    checked={selectedPrinterType === option.value}
                                    onChange={(e) => setSelectedPrinterType(e.target.value)}
                                    disabled={!option.enabled}
                                    style={{ marginRight: '0.5rem' }}
                                />
                                <div>
                                    <strong>{option.label}</strong>
                                    {!option.enabled && <span style={{ color: theme?.colors?.error }}> (Not Available)</span>}
                                    <br />
                                    <small style={{ color: theme?.colors?.textSecondary }}>
                                        {option.description}
                                    </small>
                                </div>
                            </label>
                        </div>
                    ))}
                </div>

                {/* Width Selection */}
                <div style={sectionStyle}>
                    <h3 style={{ marginTop: 0, color: theme?.colors?.text }}>📏 {t('paperWidth')}</h3>
                    {widthOptions.map(option => (
                        <div key={option.value} style={{ marginBottom: '0.5rem' }}>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                padding: '0.5rem',
                                backgroundColor: selectedWidth === option.value ?
                                    (theme?.colors?.primaryLight || '#e3f2fd') : 'transparent',
                                borderRadius: theme?.borderRadius || 4
                            }}>
                                <input
                                    type="radio"
                                    value={option.value}
                                    checked={selectedWidth === option.value}
                                    onChange={(e) => setSelectedWidth(Number(e.target.value))}
                                    style={{ marginRight: '0.5rem' }}
                                />
                                <div>
                                    <strong>{option.label}</strong>
                                    <br />
                                    <small style={{ color: theme?.colors?.textSecondary }}>
                                        {option.description}
                                    </small>
                                </div>
                            </label>
                        </div>
                    ))}
                </div>

                {/* Baud Rate Selection */}
                <div style={sectionStyle}>
                    <h3 style={{ marginTop: 0, color: theme?.colors?.text }}>⚙️ {t('baudRate')}</h3>
                    {baudRateOptions.map(option => (
                        <div key={option.value} style={{ marginBottom: '0.5rem' }}>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                padding: '0.5rem',
                                backgroundColor: selectedBaudRate === option.value ?
                                    (theme?.colors?.primaryLight || '#e3f2fd') : 'transparent',
                                borderRadius: theme?.borderRadius || 4
                            }}>
                                <input
                                    type="radio"
                                    value={option.value}
                                    checked={selectedBaudRate === option.value}
                                    onChange={(e) => setSelectedBaudRate(Number(e.target.value))}
                                    style={{ marginRight: '0.5rem' }}
                                />
                                <div>
                                    <strong>{option.label}</strong>
                                    <br />
                                    <small style={{ color: theme?.colors?.textSecondary }}>
                                        {option.description}
                                    </small>
                                </div>
                            </label>
                        </div>
                    ))}
                </div>

                {/* Font Size Selection */}
                <div style={sectionStyle}>
                    <h3 style={{ marginTop: 0, color: theme?.colors?.text }}>🔤 {t('fontSize')}</h3>
                    {fontSizeOptions.map(option => (
                        <div key={option.value} style={{ marginBottom: '0.5rem' }}>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                padding: '0.5rem',
                                backgroundColor: selectedFontSize === option.value ?
                                    (theme?.colors?.primaryLight || '#e3f2fd') : 'transparent',
                                borderRadius: theme?.borderRadius || 4
                            }}>
                                <input
                                    type="radio"
                                    value={option.value}
                                    checked={selectedFontSize === option.value}
                                    onChange={(e) => setSelectedFontSize(e.target.value)}
                                    style={{ marginRight: '0.5rem' }}
                                />
                                <div>
                                    <strong>{option.label}</strong>
                                    <br />
                                    <small style={{ color: theme?.colors?.textSecondary }}>
                                        {option.description}
                                    </small>
                                </div>
                            </label>
                        </div>
                    ))}
                </div>

                {/* Test Sections Selection */}
                <div style={sectionStyle}>
                    <h3 style={{ marginTop: 0, color: theme?.colors?.text }}>✓ {t('testSections')}</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            padding: '0.5rem',
                            backgroundColor: testSections.patterns ? (theme?.colors?.primaryLight || '#e3f2fd') : 'transparent',
                            borderRadius: theme?.borderRadius || 4
                        }}>
                            <input
                                type="checkbox"
                                checked={testSections.patterns}
                                onChange={(e) => setTestSections({...testSections, patterns: e.target.checked})}
                                style={{ marginRight: '0.5rem' }}
                            />
                            <strong>{t('patterns')}</strong>
                        </label>

                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            padding: '0.5rem',
                            backgroundColor: testSections.alignment ? (theme?.colors?.primaryLight || '#e3f2fd') : 'transparent',
                            borderRadius: theme?.borderRadius || 4
                        }}>
                            <input
                                type="checkbox"
                                checked={testSections.alignment}
                                onChange={(e) => setTestSections({...testSections, alignment: e.target.checked})}
                                style={{ marginRight: '0.5rem' }}
                            />
                            <strong>{t('alignment')}</strong>
                        </label>

                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            padding: '0.5rem',
                            backgroundColor: testSections.columns ? (theme?.colors?.primaryLight || '#e3f2fd') : 'transparent',
                            borderRadius: theme?.borderRadius || 4
                        }}>
                            <input
                                type="checkbox"
                                checked={testSections.columns}
                                onChange={(e) => setTestSections({...testSections, columns: e.target.checked})}
                                style={{ marginRight: '0.5rem' }}
                            />
                            <strong>{t('columns')}</strong>
                        </label>

                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            padding: '0.5rem',
                            backgroundColor: testSections.border ? (theme?.colors?.primaryLight || '#e3f2fd') : 'transparent',
                            borderRadius: theme?.borderRadius || 4
                        }}>
                            <input
                                type="checkbox"
                                checked={testSections.border}
                                onChange={(e) => setTestSections({...testSections, border: e.target.checked})}
                                style={{ marginRight: '0.5rem' }}
                            />
                            <strong>{t('border')}</strong>
                        </label>

                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            padding: '0.5rem',
                            backgroundColor: testSections.qrCode ? (theme?.colors?.primaryLight || '#e3f2fd') : 'transparent',
                            borderRadius: theme?.borderRadius || 4
                        }}>
                            <input
                                type="checkbox"
                                checked={testSections.qrCode}
                                onChange={(e) => setTestSections({...testSections, qrCode: e.target.checked})}
                                style={{ marginRight: '0.5rem' }}
                            />
                            <strong>{t('qrCode')}</strong>
                        </label>
                    </div>
                </div>

                {/* Status Display */}
                {testStatus && (
                    <div style={{
                        padding: '1rem',
                        marginBottom: '1rem',
                        backgroundColor: testStatus.includes(t('error')) ?
                            (theme?.colors?.errorLight || '#ffebee') :
                            (theme?.colors?.successLight || '#e8f5e9'),
                        color: testStatus.includes(t('error')) ?
                            (theme?.colors?.error || '#d32f2f') :
                            (theme?.colors?.success || '#2e7d32'),
                        borderRadius: theme?.borderRadius || 8,
                        textAlign: 'center',
                        fontWeight: 'bold'
                    }}>
                        {testStatus}
                    </div>
                )}

                {/* Action Buttons */}
                <div style={{
                    display: 'flex',
                    gap: '1rem',
                    flexWrap: 'wrap',
                    marginTop: '1.5rem'
                }}>
                    <button
                        style={buttonStyle}
                        onClick={handleTestPrint}
                        disabled={isLoading || (selectedPrinterType === 'bluetooth' ? !isBluetoothSupported : !isSerialSupported)}
                    >
                        {isLoading ? t('printing') + '...' : '🖨️ ' + t('printTest')}
                    </button>
                    <button
                        style={{
                            ...buttonStyle,
                            backgroundColor: theme?.colors?.success || '#4caf50'
                        }}
                        onClick={handleSaveConfig}
                    >
                        💾 {t('saveSettings')}
                    </button>
                    <button
                        style={{
                            ...buttonStyle,
                            backgroundColor: theme?.colors?.textSecondary || '#666'
                        }}
                        onClick={onClose}
                    >
                        ✖️ {t('close')}
                    </button>
                </div>

                {/* Diagnostics Section */}
                {!isSerialSupported && diagnosticInfo && (
                    <details style={{
                        marginTop: '1.5rem',
                        padding: '1rem',
                        backgroundColor: theme?.colors?.background || '#f5f5f5',
                        borderRadius: theme?.borderRadius || 8,
                        fontSize: '0.85rem',
                        color: theme?.colors?.textSecondary || '#666',
                        border: `1px solid ${theme?.colors?.border || '#ddd'}`
                    }}>
                        <summary style={{
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            color: theme?.colors?.text || '#000',
                            marginBottom: '0.5rem'
                        }}>
                            🔍 {t('viewDiagnostics')} (Click to expand)
                        </summary>
                        <pre style={{
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            backgroundColor: theme?.colors?.surface || '#fff',
                            padding: '0.5rem',
                            borderRadius: '4px',
                            overflow: 'auto',
                            margin: '0.5rem 0 0 0'
                        }}>
                            {diagnosticInfo}
                        </pre>
                    </details>
                )}

                {/* Help Text */}
                <div style={{
                    marginTop: '1.5rem',
                    padding: '1rem',
                    backgroundColor: theme?.colors?.background || '#f5f5f5',
                    borderRadius: theme?.borderRadius || 8,
                    fontSize: '0.9rem',
                    color: theme?.colors?.textSecondary || '#666'
                }}>
                    <strong>💡 {t('tip')}:</strong>
                    <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                        <li>{t('tryQuickTests')}</li>
                        <li>{t('pt210Recommended28')}</li>
                        <li>{t('ifTextCutOff')}</li>
                        <li>{t('saveBestSettings')}</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}

export default PrintTestModal;
